import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { obtenerPreferencias, guardarPreferencias } from '../../services/minutasV2Service';

/**
 * tema.js — v2
 *
 * Los colores de la interfaz v2 y el modo claro/oscuro, en un sitio.
 *
 * Hay tres preferencias posibles —`claro`, `oscuro` y `sistema`— y dos modos
 * reales. `sistema` no es un tercer aspecto: es delegar en el ajuste del sistema
 * operativo, que además puede cambiar solo (muchos equipos pasan a oscuro al
 * anochecer). Por eso se escucha `prefers-color-scheme` mientras la app está
 * abierta, en vez de leerlo una vez al arrancar.
 *
 * Dónde se guarda la elección, y por qué en dos sitios:
 *
 *  - En `localStorage`, con la clave del usuario: se aplica en el primer
 *    fotograma, sin esperar a la red, y sigue funcionando con la base de datos
 *    caída. La clave lleva el `userId` porque dos personas pueden usar el mismo
 *    navegador y la preferencia es de cada una, no del equipo.
 *  - En la base de datos: para que la elección acompañe a la persona a otro
 *    equipo. Es la fuente de verdad, pero llega tarde, así que se aplica cuando
 *    responde.
 *
 * Las pantallas no leen colores de aquí directamente: piden `useTema()` y
 * construyen sus estilos con la paleta que toque.
 */

/** La paleta oscura es la que la v2 ha tenido siempre; no cambia. */
const OSCURO = {
  modo: 'oscuro',
  fondo: '#0f172a',
  fondoHondo: '#0b1220',
  panel: '#1e293b',
  panelAlto: '#273449',
  borde: '#334155',
  texto: '#f1f5f9',
  textoFuerte: '#ffffff',
  suave: '#94a3b8',
  tenue: '#64748b',
  acento: '#3b82f6',
  acentoTexto: '#ffffff',
  acentoSuave: '#1e3a5f',
  acentoClaro: '#60a5fa',
  exito: '#10b981',
  atencion: '#f59e0b',
  error: '#ef4444',
  sombra: 'none',
  gradiente: 'linear-gradient(135deg, #1e3a5f 0%, #2d1b4e 100%)',
  velo: 'rgba(0,0,0,.75)'
};

/** El claro es su contrapartida en blanco, con los mismos papeles. */
const CLARO = {
  modo: 'claro',
  fondo: '#ffffff',
  fondoHondo: '#f8fafc',
  panel: '#ffffff',
  panelAlto: '#f1f5f9',
  borde: '#e2e8f0',
  texto: '#0f172a',
  textoFuerte: '#000000',
  suave: '#475569',
  tenue: '#64748b',
  acento: '#2563eb',
  acentoTexto: '#ffffff',
  acentoSuave: '#eff6ff',
  acentoClaro: '#1d4ed8',
  exito: '#059669',
  atencion: '#b45309',
  error: '#dc2626',
  // En claro, un panel blanco sobre fondo blanco necesita algo más que el borde
  // para leerse como una superficie levantada.
  sombra: '0 1px 2px rgba(15, 23, 42, .06)',
  gradiente: 'linear-gradient(135deg, #dbeafe 0%, #e9d5ff 100%)',
  velo: 'rgba(15,23,42,.72)'
};

export const PALETAS = { oscuro: OSCURO, claro: CLARO };

export const FUENTE = 'Segoe UI, system-ui, -apple-system, sans-serif';

export const PREFERENCIAS = [
  { id: 'claro', etiqueta: 'Claro', icono: '☀' },
  { id: 'oscuro', etiqueta: 'Oscuro', icono: '☾' },
  { id: 'sistema', etiqueta: 'Sistema', icono: '🖥' }
];

const VALIDAS = PREFERENCIAS.map(p => p.id);

/** Recuadro de aviso, en el tono que corresponda a la paleta activa. */
export function avisoCon(C, tipo = 'info') {
  const tonos = C.modo === 'oscuro'
    ? {
      error: { fondo: '#3f1d1d', texto: '#fca5a5', borde: '#7f1d1d' },
      exito: { fondo: '#14312a', texto: '#6ee7b7', borde: '#065f46' },
      atencion: { fondo: '#3b2c12', texto: '#fcd34d', borde: '#78350f' },
      info: { fondo: C.acentoSuave, texto: '#bfdbfe', borde: '#1e40af' }
    }
    : {
      error: { fondo: '#fef2f2', texto: '#991b1b', borde: '#fecaca' },
      exito: { fondo: '#f0fdf4', texto: '#166534', borde: '#bbf7d0' },
      atencion: { fondo: '#fffbeb', texto: '#92400e', borde: '#fde68a' },
      info: { fondo: C.acentoSuave, texto: '#1e40af', borde: '#bfdbfe' }
    };

  const tono = tonos[tipo] || tonos.info;
  return {
    padding: '11px 14px',
    borderRadius: 9,
    fontSize: 13,
    marginBottom: 14,
    lineHeight: 1.5,
    background: tono.fondo,
    color: tono.texto,
    border: `1px solid ${tono.borde}`
  };
}

const claveDe = (userId) => `minutas-v2-tema:${userId || 'anonimo'}`;

function leerGuardada(userId) {
  try {
    const valor = window.localStorage.getItem(claveDe(userId));
    return VALIDAS.includes(valor) ? valor : null;
  } catch {
    return null;
  }
}

/** Lo que el sistema operativo pide ahora mismo. */
function modoDelSistema() {
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'oscuro'
      : 'claro';
  } catch {
    return 'oscuro';
  }
}

const TemaContexto = createContext(null);

/**
 * @param {Object} props
 * @param {string|null} props.userId - identificador de la cuenta; separa la
 *   preferencia de cada persona y permite guardarla en la base de datos
 */
export function TemaProvider({ userId = null, children }) {
  const [preferencia, setPreferencia] = useState(() => leerGuardada(userId) || 'sistema');
  const [delSistema, setDelSistema] = useState(modoDelSistema);
  const [sincronizando, setSincronizando] = useState(false);

  // Al cambiar de cuenta, la preferencia que manda es la de esa cuenta.
  useEffect(() => {
    const guardada = leerGuardada(userId);
    if (guardada) setPreferencia(guardada);
  }, [userId]);

  // El sistema puede cambiar mientras la app está abierta.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const consulta = window.matchMedia('(prefers-color-scheme: dark)');
    const alCambiar = (e) => setDelSistema(e.matches ? 'oscuro' : 'claro');

    // Safari antiguo no tiene addEventListener en MediaQueryList.
    if (consulta.addEventListener) consulta.addEventListener('change', alCambiar);
    else consulta.addListener(alCambiar);

    return () => {
      if (consulta.removeEventListener) consulta.removeEventListener('change', alCambiar);
      else consulta.removeListener(alCambiar);
    };
  }, []);

  // La preferencia guardada en el servidor acompaña a la persona a otro equipo.
  // Llega tarde a propósito: primero se pinta con lo local, y esto la corrige si
  // difiere. Si la base de datos no está, no pasa nada.
  useEffect(() => {
    if (!userId) return undefined;
    let vigente = true;

    obtenerPreferencias(userId)
      .then(prefs => {
        if (!vigente || !prefs || !VALIDAS.includes(prefs.tema)) return;
        if (leerGuardada(userId)) return; // lo elegido aquí ya es lo más reciente
        setPreferencia(prefs.tema);
      })
      .catch(() => { /* sin servidor, vale lo local */ });

    return () => { vigente = false; };
  }, [userId]);

  const modo = preferencia === 'sistema' ? delSistema : preferencia;
  const C = PALETAS[modo] || OSCURO;

  // El navegador pinta detrás de la app: sin esto, al hacer scroll de rebote o
  // en el hueco bajo un contenido corto asoma el fondo anterior.
  useEffect(() => {
    const anterior = document.body.style.background;
    document.body.style.background = C.fondo;
    document.body.style.colorScheme = modo === 'oscuro' ? 'dark' : 'light';
    return () => { document.body.style.background = anterior; };
  }, [C.fondo, modo]);

  const cambiar = useCallback((nueva) => {
    if (!VALIDAS.includes(nueva)) return;
    setPreferencia(nueva);

    try {
      window.localStorage.setItem(claveDe(userId), nueva);
    } catch {
      /* sin almacenamiento local, la elección dura lo que la sesión */
    }

    if (!userId) return;
    setSincronizando(true);
    guardarPreferencias(userId, { tema: nueva })
      .catch(() => { /* queda guardada en este navegador */ })
      .finally(() => setSincronizando(false));
  }, [userId]);

  const valor = useMemo(() => ({
    C,
    modo,
    preferencia,
    sincronizando,
    cambiar,
    aviso: (tipo) => avisoCon(C, tipo)
  }), [C, modo, preferencia, sincronizando, cambiar]);

  return <TemaContexto.Provider value={valor}>{children}</TemaContexto.Provider>;
}

/**
 * La paleta activa y cómo cambiarla.
 *
 * Fuera del proveedor devuelve la paleta oscura y un `cambiar` que no hace
 * nada: así un componente suelto (una prueba, un render aislado) no revienta.
 */
export function useTema() {
  const contexto = useContext(TemaContexto);
  if (contexto) return contexto;

  return {
    C: OSCURO,
    modo: 'oscuro',
    preferencia: 'oscuro',
    sincronizando: false,
    cambiar: () => {},
    aviso: (tipo) => avisoCon(OSCURO, tipo)
  };
}

const tema = { PALETAS, FUENTE, PREFERENCIAS, TemaProvider, useTema, avisoCon };
export default tema;
